import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AgentConfig, AgentKpiConfig } from '../models/agent-config.model';
import { AgentDefinition } from '../models/agent-definition.model';
import { SensitivityLevel, SensitivityMeta } from '../models/agent-sensitivity.model';
import { AgentCadence, AgentKpiDirection } from '../models/agent-taxonomy.model';
import { AgentKpi } from '../models/agent-kpi.model';
import { AGENT_BASELINE_LABEL, AGENT_DIRECTION_LABEL, AGENT_SENSITIVITY_META } from './agent-taxonomy.constants';
import { buildAgentDefinitionsFixture } from './agent-definitions.fixture';

/**
 * Owns the agent catalogue and the configuration a reviewer sets on each agent.
 *
 * The catalogue is definitions in, UI out: every card, tab and control renders from an
 * `AgentDefinition`, so a fourth agent is a new definition and no new UI. There is no endpoint yet,
 * and `fetchAgents` is the single seam that replaces — the same shape as
 * `AlertsService.fetchWaitingAlerts`.
 *
 * **Configuration is held in memory only.** A refresh resets it, which is deliberate: agent config
 * is domain data belonging to the endpoint, not UI state, so it is not put in
 * `UiStateStorageService` (which is per-browser and meant for grid columns and tab selections).
 */
@Injectable({
  providedIn: 'root'
})
export class AgentsService {
  private readonly _agents = new BehaviorSubject<AgentDefinition[]>([]);

  /** The agent catalogue, in the order the source returns it. */
  public readonly agents$: Observable<AgentDefinition[]> = this._agents.asObservable();

  private readonly _configChanges = new BehaviorSubject<void>(undefined);

  /** Emits whenever any agent's configuration changes, so a view can re-read what it needs. */
  public readonly configChanges$: Observable<void> = this._configChanges.asObservable();

  /** What has been set, per agent. Absent means the agent is still on its definition's defaults. */
  private readonly _configByAgentId = new Map<string, AgentConfig>();

  /** Above this many words, the mark takes only the first three initials. */
  private static readonly MAX_MARK_LETTERS = 3;

  /** Loads the catalogue. */
  loadAgents(): void {
    this.fetchAgents().subscribe(agents => this._agents.next(agents));
  }

  /** Current catalogue without subscribing. */
  getAgents(): AgentDefinition[] {
    return this._agents.value;
  }

  getAgent(agentId: string): AgentDefinition | undefined {
    return this._agents.value.find(agent => agent.id === agentId);
  }

  /**
   * The agent's live configuration.
   *
   * Built from the definition's defaults and then overlaid with what was set, so a changed default
   * reaches every agent nobody has configured without a migration.
   */
  configFor(agentId: string): AgentConfig {
    const existing = this._configByAgentId.get(agentId);
    if (existing) {
      return existing;
    }

    const created = this.buildDefaultConfig(agentId);
    this._configByAgentId.set(agentId, created);
    return created;
  }

  /** One KPI's live configuration. */
  kpiConfigFor(agentId: string, kpiId: string): AgentKpiConfig | undefined {
    return this.configFor(agentId).kpis[kpiId];
  }

  setMaxPerDay(agentId: string, maxPerDay: number | null): void {
    this.updateConfig(agentId, config => ({ ...config, maxPerDay }));
  }

  setGroupByCause(agentId: string, groupByCause: boolean): void {
    this.updateConfig(agentId, config => ({ ...config, groupByCause }));
  }

  setCadence(agentId: string, cadence: AgentCadence): void {
    this.updateConfig(agentId, config => ({ ...config, cadence }));
  }

  setDetectionOption(agentId: string, optionId: string, isEnabled: boolean): void {
    this.updateConfig(agentId, config => ({
      ...config,
      detectionOptions: { ...config.detectionOptions, [optionId]: isEnabled }
    }));
  }

  setKpiEnabled(agentId: string, kpiId: string, isEnabled: boolean): void {
    this.updateKpiConfig(agentId, kpiId, kpi => ({ ...kpi, isEnabled }));
  }

  setKpiSensitivity(agentId: string, kpiId: string, sensitivity: SensitivityLevel): void {
    this.updateKpiConfig(agentId, kpiId, kpi => ({ ...kpi, sensitivity }));
  }

  setKpiDirection(agentId: string, kpiId: string, direction: AgentKpiDirection): void {
    this.updateKpiConfig(agentId, kpiId, kpi => ({ ...kpi, direction }));
  }

  /**
   * The agent's initials mark, e.g. `Service Level Anomaly Detection` → `SLA`.
   *
   * Lives here rather than in a component because the DEO widget's agent selector needs the same
   * mark for the same agent.
   */
  resolveAgentMark(name: string): string {
    return (name || '')
      .split(/\s+/)
      .filter(word => !!word)
      .slice(0, AgentsService.MAX_MARK_LETTERS)
      .map(word => word[0].toUpperCase())
      .join('');
  }

  getSensitivityMeta(level: SensitivityLevel): SensitivityMeta {
    return AGENT_SENSITIVITY_META[level];
  }

  /** `Balanced · drops only` — the one-line summary a KPI row carries. */
  describeKpiConfig(kpiConfig: AgentKpiConfig): string {
    return `${AGENT_SENSITIVITY_META[kpiConfig.sensitivity].label} · ${AGENT_DIRECTION_LABEL[kpiConfig.direction]}`;
  }

  /**
   * The threshold the agent will actually use: the KPI's own threshold scaled by the sensitivity
   * factor. This is the only place the factor surfaces, under "What this means technically".
   */
  describeDerivedThreshold(kpi: AgentKpi, sensitivity: SensitivityLevel): string {
    const scaled = Math.round(kpi.defaults.threshold * AGENT_SENSITIVITY_META[sensitivity].factor * 10) / 10;
    const unit = kpi.defaults.measure === 'relative' ? '%' : ' pp';
    return `${scaled}${unit} change against baseline`;
  }

  describeDerivedBaseline(kpi: AgentKpi): string {
    return `${AGENT_BASELINE_LABEL[kpi.defaults.baseline]} — per-SKUL median, demand-weighted in aggregate`;
  }

  describeDerivedWindow(kpi: AgentKpi): string {
    return `${kpi.defaults.windowLength} ${kpi.defaults.windowUnit} trailing, floored 4wk and capped 26wk`;
  }

  /**
   * The one place the catalogue comes from.
   *
   * Replace this body with the HTTP call when the endpoint lands; nothing else reads the fixture.
   */
  private fetchAgents(): Observable<AgentDefinition[]> {
    return of(buildAgentDefinitionsFixture());
  }

  private buildDefaultConfig(agentId: string): AgentConfig {
    const definition = this.getAgent(agentId);

    return {
      maxPerDay: 10,
      groupByCause: true,
      cadence: definition?.defaultCadence ?? 'weekly',
      detectionOptions: this.buildDefaultDetectionOptions(definition),
      kpis: this.buildDefaultKpiConfigs(definition)
    };
  }

  private buildDefaultDetectionOptions(definition?: AgentDefinition): Record<string, boolean> {
    const options: Record<string, boolean> = {};
    definition?.detectionOptions.forEach(option => (options[option.id] = option.enabledByDefault));
    return options;
  }

  private buildDefaultKpiConfigs(definition?: AgentDefinition): Record<string, AgentKpiConfig> {
    const kpis: Record<string, AgentKpiConfig> = {};

    definition?.kpis.forEach(kpi => {
      kpis[kpi.id] = {
        isEnabled: kpi.enabledByDefault,
        sensitivity: kpi.defaults.sensitivity,
        direction: kpi.defaults.direction
      };
    });

    return kpis;
  }

  private updateConfig(agentId: string, change: (config: AgentConfig) => AgentConfig): void {
    this._configByAgentId.set(agentId, change(this.configFor(agentId)));
    this._configChanges.next();
  }

  private updateKpiConfig(agentId: string, kpiId: string, change: (kpiConfig: AgentKpiConfig) => AgentKpiConfig): void {
    const existing = this.configFor(agentId).kpis[kpiId];
    if (!existing) {
      return;
    }

    this.updateConfig(agentId, config => ({
      ...config,
      kpis: { ...config.kpis, [kpiId]: change(existing) }
    }));
  }
}
